import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton'; // Use StepButton for clicking
import Box from '@mui/material/Box';
import SubSteps11 from '../SubSteps/SubSteps1/SubSteps11';
import ViewGA from './ViewGA';
import ViewCertification from './ViewCertification';
import { Container } from 'react-trello';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCertificate,
  faUserGroup,
  faCheckDouble,
} from '@fortawesome/pro-duotone-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Typography, Button as MuiButton, Chip } from '@mui/material';
import { advanceSearch, advanceSearchDashboard } from '../../../_helper/services/advanceSearchv2';
import CertificationStatusStepper from './CertificationStatusStepper';
import { ShowError } from '../../../_helper/helper';
import { getFormalApiErrorMessage } from '../../../utils/apiError';

// Import your Step components
// import CreateGA from './CreateGA';
// import ListOfGA from './ListOfGA';
// import ListOfCertification from './ListOfCertification';

const steps = ['Create GA', 'List of GA', 'List of certification'];

// Shared sizing (~1.5x the MUI "small" default) for every button/chip that
// sits in the "Step 3: View certification" header row, next to the heading.
// Any new action added to that row should reuse these so it stays visually
// consistent with the others rather than needing its own one-off sizing.
const STEP3_HEADER_ACTION_BUTTON_SX = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  borderRadius: "20px",
  fontSize: "1.1rem",
};
const STEP3_HEADER_ACTION_CHIP_SX = {
  fontSize: "1.1rem",
  height: "36px",
  "& .MuiChip-label": { px: 2 },
};
const STEP3_HEADER_ACTION_ICON_SIZE = "lg";
const STEP3_HEADER_ACTION_BUTTON_SIZE = "large";

function CertificationDetailsPage() {
  const history = useNavigate();
  // 0-based index for the stepper (0 = Step 1)
  const [activeStep, setActiveStep] = useState(0);
  const [certificationData, setCertificationData] = useState(null);
  const [stepCallToggle, setStepCallToggle] = useState(false);

  // 1. Get URL parameters
  const { annId } = useParams();
  const [searchParams] = useSearchParams();


  const gaId = searchParams.get('gaId');
  const certPid = searchParams.get('certPid');

  // 2. Set the *initial* active step when the component loads
  useEffect(() => {
    let initialStep = 0; // Default: Step 1

    if (gaId) {
      initialStep = 1; // Has annId + gaId: Step 2
    }

    if (gaId && certPid) {
      initialStep = 2; // Has all three: Step 3
    }

    setActiveStep(initialStep);

  }, [gaId, certPid]); // Only run this once when params load


  // 3. Handle user clicking on a step
  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const getStatus = async () => {
    if (!certPid) return;
    const tabelName = "CERT_STATUS";
    const payload = {
      tableName: tabelName,
      searchParams: JSON.stringify([
        {
          columnName: "CERTIFICATION",
          value: certPid,
          operator: "=",
          dataType: "VARCHAR(50)",
        },
      ]),
      columns: "*",
      pageNumber: 1,
      pageSize: 10000,
      sortData: "",
      additionalWhere: "",
      location: "pmis-global",
      id: tabelName,
    };
    try {
      //   const response = await advanceSearchDashboard(payload, tableName);
      //   setSummaryData(response?.results || []); // Adjusted to use response.results
      const response = await advanceSearch(payload);
      if (response?.results && response.results.length > 0) {
        setCertificationData(response.results[0]);
      } else {
        setCertificationData(null);
      }
    } catch (error) {
      console.log(error);
      ShowError(
        getFormalApiErrorMessage(
          error,
          'We are unable to load certification status details at this time. Please try again shortly.',
          { suppressNotFound: true }
        )
      );
      setCertificationData(null);
    } finally {
    }
  };

  useEffect(() => {
    if (activeStep === 2 && certPid) {
      getStatus();
    }
  }, [certPid, activeStep, stepCallToggle]);


  // 4. Render your stepper and content
  return (
    <Container fluid={true}>
      <Row className="bg-primary rounded py-2 mb-3 sticky-top" style={{ zIndex: 0 }}>
        <Col className="d-flex align-items-center gap-2">
          <Button
            color="light"
            outline
            size="sm"
            className="d-flex align-items-center gap-1 text-white"
            style={{ borderColor: "#fff" }}
            onClick={() => history('/dashboard/certificate-tracking')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </Button>
          <FontAwesomeIcon icon={faCertificate} size="xl" />
          <Typography variant="h6" style={{ color: "white" }}>
            Certification Detail
          </Typography>
        </Col>
      </Row>
      <Row>
        {/* --- Clickable MUI Stepper --- */}
        <Box sx={{ width: "100%", marginBottom: "2rem" }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepButton
                  color="inherit"
                  onClick={() => handleStepClick(index)}
                >
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Row>
      <Card>
        <CardBody>
          <div>
            {/* --- Render Content Based on Active Step --- */}
            <div>
              {activeStep === 0 && (
                <div>
                  <h3>Step 1: Create GA</h3>
                  {/* <CreateGA annId={annId} /> */}
                  {/* Pass annId to your component */}
                  <SubSteps11
                    annId={annId}
                    setSubstepes={() => setActiveStep(1)}
                  />
                </div>
              )}
              {activeStep === 1 && (
                <div>
                  {/* <ListOfGA annId={annId} gaId={gaId} /> */}
                  {/* Pass annId and gaId to your component */}
                  <ViewGA
                    annId={annId}
                    gaId={gaId}
                    setSubstepes={() => setActiveStep(2)}
                  />
                </div>
              )}
              {activeStep === 2 && (
                <div>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3>Step 3: View certification</h3>
                    {certificationData && (
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        {!certificationData.Hr_Aso && (
                          <Chip
                            label="Assign ASO from the Dashboard"
                            color="info"
                            sx={STEP3_HEADER_ACTION_CHIP_SX}
                          />
                        )}
                        {!certificationData.DOA_Director_Approvel_request && (
                          <Chip
                            label="Waiting for DOA Director Approval"
                            color="warning"
                            sx={STEP3_HEADER_ACTION_CHIP_SX}
                          />
                        )}
                        <MuiButton
                          color="primary"
                          size={STEP3_HEADER_ACTION_BUTTON_SIZE}
                          variant="outlined"
                          onClick={() =>
                            history(
                              `/certificate-tracking/${certificationData.ANNOUNCEMENT_ID?.trim()}/${certificationData.CERTIFICATION_PID?.trim()}`
                            )
                          }
                          sx={STEP3_HEADER_ACTION_BUTTON_SX}
                        >
                          <FontAwesomeIcon
                            color="var(--theme-default)"
                            icon={faUserGroup}
                            size={STEP3_HEADER_ACTION_ICON_SIZE}
                          />{" "}
                          View Applicants
                        </MuiButton>
                        {certificationData.Return_To_HR &&
                          certificationData.App_Assigned && (
                            <MuiButton
                              color="primary"
                              size={STEP3_HEADER_ACTION_BUTTON_SIZE}
                              variant="outlined"
                              onClick={() =>
                                history(
                                  `/certificate-tracking/${certificationData.ANNOUNCEMENT_ID?.trim()}/${certificationData.CERTIFICATION_PID?.trim()}`,
                                  { state: { updateGG1: true } }
                                )
                              }
                              sx={STEP3_HEADER_ACTION_BUTTON_SX}
                            >
                              <FontAwesomeIcon
                                color="var(--theme-default)"
                                icon={faCheckDouble}
                                size={STEP3_HEADER_ACTION_ICON_SIZE}
                              />
                              Assign GG1 Recruitment
                            </MuiButton>
                          )}
                      </div>
                    )}
                  </div>
                  {/* <ListOfCertification annId={annId} gaId={gaId} certPid={certPid} /> */}
                  {/* Pass all props to your component */}

                  {certificationData && (
                    <CertificationStatusStepper data={certificationData} />
                  )}

                  <ViewCertification
                    annId={annId}
                    gaId={gaId}
                    certPid={certPid}
                    callUpdateStep={() => setStepCallToggle(!stepCallToggle)}
                  />
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

export default CertificationDetailsPage;
